import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Button, DataList, Flex, Select, Text } from '@radix-ui/themes'
import { Pencil1Icon, PlusIcon } from '@radix-ui/react-icons'
import { FieldShell } from '~/components/FieldShell'
import { useTRPC } from '~/utils/trpc'
import { ContactFormDialog, type ContactSummary } from './ContactFormDialog'

const NO_CONTACT = '__none__'

export interface ContactPickerProps {
  orgId: string
  value: string | null | undefined
  onChange: (contactId: string | null) => void
  disabled?: boolean
  label?: string
}

export const ContactPicker = ({
  orgId,
  value,
  onChange,
  disabled = false,
  label = 'Shipping contact'
}: ContactPickerProps) => {
  const trpc = useTRPC()
  const [dialog, setDialog] = useState<{ open: boolean; contact: ContactSummary | null }>({
    open: false,
    contact: null
  })

  const contactsQuery = useQuery(trpc.ops.contacts.list.queryOptions({ orgId }))
  const contacts = contactsQuery.data ?? []
  const selected = contacts.find((contact) => contact.id === value) ?? null

  return (
    <Flex direction="column" gap="3">
      <FieldShell
        label={label}
        action={
          <Flex gap="3" align="center">
            {selected ? (
              <Button
                type="button"
                size="1"
                variant="ghost"
                color="gray"
                disabled={disabled}
                onClick={() => setDialog({ open: true, contact: selected })}
              >
                <Pencil1Icon />
                Edit
              </Button>
            ) : null}
            <Button
              type="button"
              size="1"
              variant="ghost"
              disabled={disabled}
              onClick={() => setDialog({ open: true, contact: null })}
            >
              <PlusIcon />
              New
            </Button>
          </Flex>
        }
      >
        <Select.Root
          value={value ?? NO_CONTACT}
          disabled={disabled}
          onValueChange={(v) => onChange(v === NO_CONTACT ? null : v)}
        >
          <Select.Trigger className="w-full" />
          <Select.Content>
            <Select.Item value={NO_CONTACT}>—</Select.Item>
            {contacts.map((contact) => (
              <Select.Item key={contact.id} value={contact.id}>
                {contact.name}
                {contact.email ? ` · ${contact.email}` : ''}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
      </FieldShell>

      {selected ? (
        <DataList.Root size="1">
          <DataList.Item>
            <DataList.Label>Email</DataList.Label>
            <DataList.Value>{selected.email ?? '—'}</DataList.Value>
          </DataList.Item>
          <DataList.Item>
            <DataList.Label>Phone</DataList.Label>
            <DataList.Value>{selected.phone ?? '—'}</DataList.Value>
          </DataList.Item>
          <DataList.Item>
            <DataList.Label>Address</DataList.Label>
            <DataList.Value>{selected.address ?? '—'}</DataList.Value>
          </DataList.Item>
        </DataList.Root>
      ) : (
        <Text size="1" color="gray">
          No contact selected.
        </Text>
      )}

      <ContactFormDialog
        open={dialog.open}
        onOpenChange={(open) => setDialog((prev) => ({ ...prev, open }))}
        orgId={orgId}
        contact={dialog.contact}
        onSaved={(contactId) => onChange(contactId)}
      />
    </Flex>
  )
}
